import { Grid, Switch } from '@mui/material';
import { HSpacer, RoutalDialog, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { Subscription } from '../../../../../domain/backoffice/subscription/Subscription';
import Button from '../../../../../react-components/Button';
import InputText from '../../../../../react-components/InputText';
import Text from '../../../../../react-components/Text';
import { useUpdateSubscriptionSMS } from '../../../hooks/subscriptions/useUpdateSubscriptionSMS';

type BackofficeClientsOrganizationsManageSMSDialogProps = {
  openDialog: boolean;
  organizationId: string;
  subscription: Subscription;
  onClose: (hasBeenUpdated: boolean) => void;
};

export const BackofficeClientsOrganizationsManageSMSDialog = ({
  openDialog,
  organizationId,
  subscription,
  onClose,
}: BackofficeClientsOrganizationsManageSMSDialogProps) => {
  const { run: manageSubscriptionSMS, isLoading, success } = useUpdateSubscriptionSMS();
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [increasedCredit, setIncreasedCredit] = useState<number>(0);
  const [smsFrom, setSMSFrom] = useState<string | undefined>(undefined);
  const [autoRechargeEnabled, setAutoRechargeEnabled] = useState<boolean>(false);
  const [autoRechargeAt, setAutoRechargeAt] = useState<number>(0);
  const [autoRechargeAmount, setAutoRechargeAmount] = useState<number>(0);
  const [autoRechargePrice, setAutoRechargePrice] = useState<number>(0);

  useEffect(() => {
    setIsEnabled(subscription.sms.enabled);
    setAutoRechargeAmount(subscription.sms.auto_recharge_amount);
    setAutoRechargeAt(subscription.sms.auto_recharge_at);
    setAutoRechargeEnabled(subscription.sms.auto_recharge);
    setAutoRechargePrice(subscription.sms.auto_recharge_price);
  }, [subscription]);

  useEffect(() => {
    if (success) {
      onClose(true);
    }
  }, [onClose, success]);

  if (!openDialog) return null;

  const isSaveButtonDisabled = () => {
    return (
      isLoading || (isEnabled === subscription.sms.enabled && increasedCredit === 0 && (smsFrom ?? ``).length > 11)
    );
  };

  return (
    <RoutalDialog
      open={openDialog}
      onCloseDialog={() => onClose(false)}
      dialogProps={{
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
      }}
      titleContent={<Text variant="h6" intlId="administration.users.manage_sms.title" />}
      titleRightContent={
        <Text
          variant="label"
          intlId="administration.users.manage_sms.remaining"
          intlValues={{ number: subscription.sms.credit ?? 0 }}
        />
      }
      footerContent={
        <Grid container justifyContent="flex-end" alignItems={`center`}>
          {autoRechargeEnabled && !autoRechargePrice && (
            <>
              <Text
                intlId="administration.users.manage_sms.auto_recharge_enable.warning_free"
                style={{ color: RoutalPalette.error.medium }}
              />
              <HSpacer small />
            </>
          )}

          <Button intlId="cancel" variant="flat" onClick={() => onClose(false)} />
          <HSpacer small />
          <Button
            intlId="accept"
            disabled={isSaveButtonDisabled()}
            onClick={async () => {
              manageSubscriptionSMS({
                organizationId,
                enabled: isEnabled,
                ...(smsFrom ? { from: smsFrom } : {}),
                ...((increasedCredit ?? 0) !== 0 ? { increasedCredit: increasedCredit } : {}),
                autoRechargeEnabled,
                autoRechargeAt,
                autoRechargeAmount,
                autoRechargePrice,
              });
            }}
          />
        </Grid>
      }
    >
      <Grid container direction="row" alignItems="center" justifyContent="space-between" wrap="nowrap">
        <Grid item>
          <Text intlId="administration.users.manage_sms.enable" />
        </Grid>
        <Grid item>
          <Switch
            checked={isEnabled}
            value={isEnabled}
            onChange={(event: any) => {
              const { checked } = event.target;
              setIsEnabled(checked);
            }}
            color="primary"
          />
        </Grid>
      </Grid>

      <VSpacer medium />

      <InputText
        type="number"
        label={<Text variant="label" intlId="administration.users.manage_sms.increase_sms" />}
        placeholderIntlId="administration.users.manage_sms.increase_sms_ph"
        fieldValue={increasedCredit}
        onChange={setIncreasedCredit}
      />

      <VSpacer large />
      <Grid
        style={{
          display: `flex`,
          border: `1px solid ${RoutalPalette.primary00}`,
          borderRadius: `10px`,
          flexDirection: `column`,
          padding: `16px`,
        }}
      >
        <Grid container direction="row" alignItems="center" justifyContent="space-between" wrap="nowrap">
          <Grid item>
            <Text intlId="administration.users.manage_sms.auto_recharge_enable" />
          </Grid>
          <Grid item>
            <Switch
              checked={autoRechargeEnabled}
              value={autoRechargeEnabled}
              onChange={(event: any) => {
                const { checked } = event.target;
                setAutoRechargeEnabled(checked);
              }}
              color="primary"
            />
          </Grid>
        </Grid>
        <VSpacer small />
        <Grid style={{ display: `flex` }}>
          <InputText
            type="number"
            label={<Text variant="label" intlId="administration.users.manage_sms.auto_recharge_at" />}
            fieldValue={autoRechargeAt}
            onChange={setAutoRechargeAt}
            disabled={!autoRechargeEnabled}
          />
          <HSpacer medium />
          <InputText
            type="number"
            label={<Text variant="label" intlId="administration.users.manage_sms.auto_recharge_amount" />}
            fieldValue={autoRechargeAmount}
            onChange={setAutoRechargeAmount}
            disabled={!autoRechargeEnabled}
          />
        </Grid>
        <VSpacer medium />
        <InputText
          type="number"
          label={<Text variant="label" intlId="administration.users.manage_sms.auto_recharge_price" />}
          fieldValue={autoRechargePrice}
          onChange={setAutoRechargePrice}
          disabled={!autoRechargeEnabled}
        />
      </Grid>
      <VSpacer large />

      <InputText
        label={<Text variant="label" intlId="administration.users.manage_sms.from" />}
        placeholderIntlId="administration.users.manage_sms.from_ph"
        fieldValue={smsFrom}
        onChange={setSMSFrom}
        inputProps={{
          maxLength: 11,
        }}
      />
    </RoutalDialog>
  );
};
